import { MulesoftOffering } from '../models/mulesoft-offering';

/**
 * Renders an offering as a self-contained Markdown factsheet.
 *
 * The document is built from the catalogue record the detail page already
 * holds — it is not written by a model and says nothing the page does not.
 * That is deliberate: the file leaves Panorama and gets pasted into tickets and
 * decks, so it has to be a faithful extract rather than a summary that can
 * drift from the source.
 *
 * Kept free of Angular so the rendering can be tested on its own, which is what
 * INEXP-2446 AC6 asks for.
 *
 * Output is plain CommonMark — one `#`, ATX headings, `-` bullets, no tables
 * and no front matter — because the receiving viewer is unknown and tables are
 * the first thing a minimal renderer drops.
 */

export interface OfferingMarkdownOptions {
  /** Absolute URL of the page the file came from; omitted from the footer if absent. */
  sourceUrl?: string;
  /** Injectable clock, so the footer date is assertable in tests. */
  generatedAt?: Date;
}

/**
 * Characters that change Markdown structure or open an HTML / link escape
 * hatch. Catalogue copy is Roche-internal rather than hostile, but it is
 * authored in a CMS by many hands: a stray asterisk must not italicise half the
 * factsheet, and a pasted `<img src=x onerror=…>` must not survive into a
 * viewer that renders raw HTML.
 */
const INLINE_SPECIALS = /[\\`*_[\]<>]/g;

function escapeInline(value: string): string {
  return value.replace(INLINE_SPECIALS, (char) => `\\${char}`);
}

/**
 * Neutralise markers that only mean something at the start of a line, so a
 * value beginning "# 1. Overview" stays a value instead of becoming a heading.
 * Ordered-list markers escape on the punctuation — `1\.` — since escaping the
 * digit does nothing.
 */
function neutraliseLeading(line: string): string {
  return line
    .replace(/^(\s*)([#>+\-=|])/, '$1\\$2')
    .replace(/^(\s*)(\d+)([.)])/, '$1$2\\$3');
}

/** A single-line value: escaped, whitespace collapsed so it cannot open a block. */
function inline(value: unknown): string | undefined {
  if (value === null || value === undefined) {
    return undefined;
  }
  const text = String(value).replace(/\s+/g, ' ').trim();
  return text ? escapeInline(text) : undefined;
}

/** A multi-line value: paragraph breaks kept, every line made structurally inert. */
function block(value: unknown): string | undefined {
  if (value === null || value === undefined) {
    return undefined;
  }
  const text = String(value)
    .replace(/\r\n?/g, '\n')
    .trim();
  if (!text) {
    return undefined;
  }
  return text
    .split('\n')
    .map((line) => neutraliseLeading(escapeInline(line)))
    .join('\n');
}

/**
 * Tri-state: absent stays absent. "Unknown" and "No" are different answers to
 * "is this GxP relevant", and collapsing them would be the kind of quiet
 * inaccuracy this file exists to avoid.
 */
function yesNo(value: boolean | null | undefined): string | undefined {
  if (value === null || value === undefined) {
    return undefined;
  }
  return value ? 'Yes' : 'No';
}

/** Non-empty, escaped entries of a possibly-absent string array. */
function inlineList(values: readonly (string | null | undefined)[] | null | undefined): string[] {
  return (values ?? []).map(inline).filter((v): v is string => !!v);
}

type Row = readonly [label: string, value: string | undefined];

/** Accumulates blocks, so an empty section simply never gets pushed. */
class Doc {
  private readonly _blocks: string[] = [];

  push(text: string): void {
    this._blocks.push(text);
  }

  /** A `## Heading` followed by its body — skipped entirely when the body is empty. */
  section(title: string, body: readonly string[]): void {
    const content = body.filter((part) => part.trim().length > 0);
    if (content.length === 0) {
      return;
    }
    this.push(`## ${title}`);
    content.forEach((part) => this.push(part));
  }

  toString(): string {
    return this._blocks.join('\n\n') + '\n';
  }
}

/** `- **Label:** value` lines, dropping rows whose value is absent. */
function definitions(rows: readonly Row[]): string {
  return rows
    .filter((row): row is readonly [string, string] => !!row[1])
    .map(([label, value]) => `- **${label}:** ${value}`)
    .join('\n');
}

/** `- item` lines. */
function bullets(items: readonly string[]): string {
  return items.map((item) => `- ${item}`).join('\n');
}

/**
 * A URL as a CommonMark autolink, so it is clickable without relying on the
 * viewer auto-detecting bare text. Anything that is not http(s) — the
 * catalogue also carries values like `go/box-support` — falls back to ordinary
 * escaped text.
 */
function autolink(value: string | null | undefined): string | undefined {
  const url = value?.trim();
  if (!url || !/^https?:\/\//i.test(url)) {
    return inline(value);
  }
  return `<${url.replace(/[<>\s]/g, '')}>`;
}

/**
 * `- **Label** — detail; detail` bullets. The shape most of the catalogue's
 * arrays reduce to once their empty entries are dropped.
 */
function labelled<T>(
  entries: readonly T[] | null | undefined,
  pick: (entry: T) => { label?: unknown; details?: readonly (string | undefined)[] },
): string {
  return bullets(
    (entries ?? [])
      .map((entry) => {
        const { label, details = [] } = pick(entry);
        const name = inline(label);
        if (!name) {
          return '';
        }
        const detail = details.filter((part): part is string => !!part).join('; ');
        return detail ? `**${name}** — ${detail}` : `**${name}**`;
      })
      .filter((line) => line.length > 0),
  );
}

/**
 * A named thing with optional prose — features, capabilities, use cases all
 * share this shape, so they share one renderer.
 */
function namedEntries(
  entries: readonly { name?: string | null; description?: string | null; benefit?: string | null }[]
    | null
    | undefined,
): string[] {
  return (entries ?? [])
    .map((entry) => {
      const name = inline(entry.name);
      const description = block(entry.description);
      const benefit = inline(entry.benefit);
      if (!name && !description) {
        return '';
      }
      const parts = [`### ${name ?? 'Unnamed'}`];
      if (description) {
        parts.push(description);
      }
      if (benefit) {
        parts.push(`**Benefit:** ${benefit}`);
      }
      return parts.join('\n\n');
    })
    .filter((part) => part.length > 0);
}

function singleSignOn(offering: MulesoftOffering): string | undefined {
  const sso = offering.singleSignOn;
  const supported = yesNo(sso?.isSingleSignOnSupported);
  if (!supported) {
    return undefined;
  }
  const raw = sso?.singleSignOnProviders;
  const providers = inlineList(Array.isArray(raw) ? raw : [raw]);
  return providers.length > 0 ? `${supported} (${providers.join(', ')})` : supported;
}

function costLines(offering: MulesoftOffering): string {
  const perUser = offering.cost?.perUserCost;
  // Keyed on the figure: a record carrying a currency and no number otherwise
  // renders a per-user cost of "CHF", which says nothing and reads as a bug in
  // the document rather than in the record. Noticed in the assistant's detail
  // facets, which have since been replaced by follow-up questions — this is now
  // the only renderer that reads these fields.
  const value = inline(perUser?.perUserCostValue);
  const amount = value
    ? [value, inline(perUser?.perUserCostCurrency)].filter(Boolean).join(' ')
    : '';
  const frequency = inline(perUser?.perUserCostFrequency);

  return definitions([
    ['Licence cost responsibility', inline(offering.licenseCostResponsibility)],
    ['Development cost', inline(offering.developmentCostResponsibility?.responsibility)],
    ['Support and maintenance cost', inline(offering.supportMaintenanceCostResponsibility?.responsibility)],
    ['Per-user pricing', yesNo(offering.cost?.isPerUserPricingAvailable)],
    ['Per-user cost', amount ? (frequency ? `${amount} ${frequency}` : amount) : undefined],
    ['Additional costs', yesNo(offering.additionalCost?.isAvailable)],
    ['Additional cost details', inline(offering.additionalCost?.additionalCostConsiderations)],
    ['Other pricing models', inline(offering.otherPricingModels?.details)],
  ]);
}

function supportLines(offering: MulesoftOffering): string[] {
  const channels = (offering.supportChannels ?? [])
    .map((channel) => {
      const label = inline(channel.serviceDeskName) ?? inline(channel.channelType);
      const destination = autolink(channel.destinationValue);
      const description = inline(channel.description);
      if (!label && !destination) {
        return '';
      }
      const detail = [destination, description].filter(Boolean).join(' — ');
      return detail ? `${label ?? 'Support'}: ${detail}` : (label as string);
    })
    .filter((line) => line.length > 0);

  const hours = (offering.supportHours ?? [])
    .map((entry) => {
      const days = inlineList(entry.days).join(', ');
      const window = [inline(entry.startTime), inline(entry.endTime)].filter(Boolean).join('–');
      const zone = inline(entry.timezone);
      const response = inline(entry.responseTime);
      const parts = [days, window, zone].filter(Boolean).join(' ');
      if (!parts && !response) {
        return '';
      }
      return response ? `${parts} (response: ${response})`.trim() : parts;
    })
    .filter((line) => line.length > 0);

  return [
    channels.length > 0 ? `**Channels**\n\n${bullets(channels)}` : '',
    hours.length > 0 ? `**Hours**\n\n${bullets(hours)}` : '',
  ];
}

function footer(offering: MulesoftOffering, options: OfferingMarkdownOptions): string {
  const generated = (options.generatedAt ?? new Date()).toISOString().slice(0, 10);
  const name = inline(offering.displayName) ?? inline(offering.offeringName) ?? 'this offering';

  // A list rather than consecutive lines: in CommonMark, lines with no blank
  // line between them are one paragraph and get soft-wrapped into a single run
  // of text. A list keeps the three notes on three lines in every viewer.
  const notes = [`Generated from the Roche Panorama catalogue on ${generated} (UTC).`];
  if (options.sourceUrl) {
    notes.push(`Source: <${options.sourceUrl}>`);
  }
  notes.push(
    `Roche internal — C2. ${name} may change; check Panorama for the current record.`,
  );

  return `---\n\n${bullets(notes)}`;
}

/**
 * Build the factsheet. Sections with nothing in them are omitted rather than
 * emitted empty, so a sparse offering yields a short, honest document instead
 * of a long one full of "N/A".
 *
 * `contentOwnership.authorEmails` is intentionally left out: the owner and
 * deputy are already public on the page, but the author list is internal
 * editorial metadata and has no business travelling in a shared file.
 *
 * Also left out are the fields that are references to other offerings —
 * successor, parent/child, related technology, similar offerings. Resolving
 * them needs the catalogue index the page builds, and an unresolved id in a
 * shared document is worse than no line at all.
 */
export function buildOfferingMarkdown(
  offering: MulesoftOffering,
  options: OfferingMarkdownOptions = {},
): string {
  const doc = new Doc();

  const title = inline(offering.displayName) ?? inline(offering.offeringName) ?? 'Offering';
  doc.push(`# ${title}`);

  const description = block(offering.description);
  if (description) {
    doc.push(description);
  }

  doc.section('At a glance', [
    definitions([
      ['Offering type', inline(offering.offeringType)],
      ['Approval status', inline(offering.approvalStatus)],
      ['Lifecycle', inline(offering.lifecycle) ?? inline(offering.lifecycleStatus)],
      ['Provider organisation', inline(offering.providerOrganization)],
      ['Data classification', inline(offering.classification?.classificationLevel)],
      ['Supported data classification', inline(offering.supportedDataClassification?.level)],
      ['Single sign-on', singleSignOn(offering)],
      ['GxP relevant', yesNo(offering.gxP)],
      ['SRA required', yesNo(offering.isSraRequired)],
      ['External use allowed', yesNo(offering.isExternalUseAllowed)],
      ['Supported', yesNo(offering.isSupported)],
      ['ArchiMEDES ID', inline(offering.archimedesID)],
    ]),
  ]);

  doc.section('Categories', [bullets(inlineList(offering.categories))]);
  doc.section('Target audience', [bullets(inlineList(offering.targetAudience))]);

  // The one place an inline value is emitted at the start of a line rather
  // than after a `- **Label:** ` prefix, so it needs the leading-marker guard a
  // definition row gets for free.
  const tags = inlineList(offering.tags?.value);
  doc.section('Tags', [tags.length > 0 ? neutraliseLeading(tags.join(', ')) : '']);

  doc.section('Key features', namedEntries(offering.features));
  doc.section('Capabilities', namedEntries(offering.capabilities));
  doc.section('Use cases', namedEntries(offering.useCases));

  doc.section('Cost', [costLines(offering)]);

  doc.section('Security and compliance', [
    definitions([
      ['Qualification status', inline(offering.qualificationStatus)],
      ['System risk assessment', inline(offering.systemRiskAssessment?.systemRiskAssessmentName)],
      ['Assessment record', autolink(offering.systemRiskAssessment?.systemRiskAssessmentUrl)],
      ['Retention period', inline(offering.classification?.classificationRetentionPeriod)],
      ['Classification policy', autolink(offering.classification?.classificationUrl)],
      [
        'Data classification notes',
        inline(offering.supportedDataClassification?.additionalInformation),
      ],
    ]),
  ]);

  doc.section('Vendor', [
    definitions([
      ['Vendor', inline(offering.vendorInfo?.vendorName)],
      ['Vendor support', inline(offering.vendorInfo?.vendorSupport)],
      ['Vendor website', autolink(offering.vendorInfo?.vendorUrl)],
    ]),
  ]);

  doc.section('Support', supportLines(offering));

  doc.section('Services and requests', [
    labelled(offering.servicesAndRequests, (service) => ({
      label: service.title,
      details: [inline(service.description), autolink(service.destinationValue)],
    })),
  ]);

  doc.section('Communities', [
    labelled(offering.community, (community) => ({
      label: community.title,
      details: [inline(community.description), autolink(community.url)],
    })),
  ]);

  doc.section('Hosting', [
    labelled(offering.hosting, (host) => ({
      label: host.hostingName,
      details: [inline(host.hostingType), inline(host.hostingManaged)],
    })),
  ]);

  doc.section('Environments', [
    labelled(offering.environment, (env) => ({
      label: env.environmentName,
      details: [
        env.serviceLevelAgreement ? `SLA: ${inline(env.serviceLevelAgreement)}` : undefined,
        autolink(env.environmentUrl),
      ],
    })),
  ]);

  doc.section('Available versions', [
    labelled(offering.availableVersions, (version) => ({
      label: version.version,
      details: [
        inline(version.status),
        version.releaseDate ? `released ${inline(version.releaseDate)}` : undefined,
        version.endOfLifeDate ? `end of life ${inline(version.endOfLifeDate)}` : undefined,
      ],
    })),
  ]);

  doc.section('Add-ons', [
    labelled(offering.addonDetails, (addon) => ({
      label: addon.addonName,
      details: [
        inline(addon.addonDescription),
        addon.addonCost ? `Cost: ${inline(addon.addonCost)}` : undefined,
        autolink(addon.addonURL),
      ],
    })),
  ]);

  const onboardingSteps = (offering.onboarding?.installationSteps ?? [])
    .map((step) => block(step.description))
    .filter((step): step is string => !!step)
    .map((step, index) => `${index + 1}. ${step}`);

  doc.section('Getting started', [
    block(offering.onboarding?.accessInstructions) ?? '',
    onboardingSteps.join('\n'),
  ]);

  doc.section('Installation', [
    labelled(offering.installationInformation, (install) => ({
      label: install.applicationType,
      details: [inline(install.instructions), autolink(install.url)],
    })),
  ]);

  doc.section('Contacts', [
    definitions([
      ['Owner', inline(offering.contentOwnership?.ownerEmail)],
      ['Deputy', inline(offering.contentOwnership?.deputyEmail)],
    ]),
  ]);

  doc.push(footer(offering, options));

  return doc.toString();
}

/**
 * File name for the downloaded factsheet. Prefers the catalogue slug, which is
 * already URL-safe and matches the page the reader came from; falls back to a
 * slugified display name so the name still identifies the offering (AC3).
 */
export function offeringMarkdownFileName(offering: MulesoftOffering): string {
  const slug = offering.slug?.trim();
  const fromName = (offering.displayName ?? offering.offeringName ?? '')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  return `${slug || fromName || 'offering'}.md`;
}
